import { useRef, useState } from "react";
import ReactSelect from 'react-select';
import { CustomSelectStyle } from './CustomSelectStyle';
import StatusPill from './StatusPill';
import Img from "../Pages/User/components/Img";

export const FormInput = ({
  label,
  name,
  value,
  onChange,
  placeholder = "",
  type = "text",
  required = false,
  rows = 1,
  isRequired = false,
  className = "",
  error = "",
  ...props
}) => {
  const isTextarea = type === 'textarea' || rows > 1;

  const handleChange = (e) => {
    onChange?.(e.target.value);
  };

  const inputClass = `border rounded px-3 py-1.5 w-full bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 transition-all duration-300
    ${error
      ? "border-red-400 dark:border-red-500 focus:ring-red-300 dark:focus:ring-red-500"
      : "border-gray-300 dark:border-gray-600 focus:ring-orange-300 dark:focus:ring-orange-400"}`;

  return (
    <div className={className}>
      <label className="block text-[13px] font-medium mb-1 text-gray-700 dark:text-gray-200">
        {label}
        {isRequired && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
      </label>
      {isTextarea ? (
        <textarea
          name={name}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          rows={rows}
          required={required}
          className={inputClass}
          {...props}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          required={required}
          className={inputClass}
          {...props}
        />
      )}
      {error && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400 flex items-center gap-1">
          <span className="w-1 h-1 rounded-full bg-red-500 inline-block flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
};

export const FormImage = ({
  label = "Upload image",
  hint = null,
  initialPreviewUrl = null,
  onChange,
  required = false,
  isRequired = false,
  className = "",
}) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(initialPreviewUrl);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);

  const validate = (f) => {
    if (required && !f && !initialPreviewUrl) {
      setError("Image is required.");
      return false;
    }
    setError("");
    return true;
  };

  const processFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setError("Please upload a valid image file.");
      onChange?.(null, false);
      return;
    }
    if (previewUrl && previewUrl !== initialPreviewUrl) URL.revokeObjectURL(previewUrl);
    const url = URL.createObjectURL(f);
    setFile(f);
    setPreviewUrl(url);
    setError("");
    onChange?.(f, true);
  };

  const handleInputChange = (e) => processFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    processFile(e.dataTransfer.files?.[0]);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (previewUrl && previewUrl !== initialPreviewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    if (inputRef.current) inputRef.current.value = "";
    const valid = validate(null);
    onChange?.(null, valid);
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-nowrap text-[13px] font-medium mb-2 text-gray-700 dark:text-gray-200">
          {label}
          {required || isRequired && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
        </label>
      )}
      {hint && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-2 -mt-1">{hint}</p>
      )}

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200 overflow-hidden
          ${dragging
            ? "border-orange-400 bg-orange-50 dark:bg-orange-500/10 scale-[1.01]"
            : error
              ? "border-red-400 dark:border-red-500 bg-red-50/40 dark:bg-red-500/5"
              : "border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-800/60 hover:border-orange-300 dark:hover:border-orange-500/50 hover:bg-orange-50/40 dark:hover:bg-orange-500/5"
          }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleInputChange}
          className="hidden"
        />

        {previewUrl ? (
          <div className="flex items-center gap-4 p-4">
            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 ring-2 ring-orange-200 dark:ring-orange-500/30">
              <Img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{file?.name ?? "Current image"}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Click to replace</p>
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="flex-shrink-0 p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20 transition"
              title="Remove"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 px-4 gap-2 select-none">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 transition-colors ${dragging ? "bg-orange-100 dark:bg-orange-500/20" : "bg-gray-100 dark:bg-slate-700"}`}>
              <svg className={`w-5 h-5 transition-colors ${dragging ? "text-orange-500" : "text-gray-400 dark:text-gray-500"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="3" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {dragging ? "Drop to upload" : "Drag & drop or click to upload"}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">PNG, JPG, WEBP, GIF up to any size</p>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1.5 text-xs text-red-500 dark:text-red-400 flex items-center gap-1">
          <span className="w-1 h-1 rounded-full bg-red-500 inline-block flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
};

export const FormSelect = ({
  label,
  value,
  multiple = false,
  onChange,
  options = [],
  required = false,
  isRequired = false,
  isClearable = true,
  className = "",
  error = "",
  ...props
}) => {
  const handleChange = (option) => {
    if (multiple) {
      onChange?.(option ? option.map((o) => o.value) : []);
    } else {
      onChange?.(option?.value ?? null);
    }
  };

  // Support grouped options ({ label, options: [...] }) by flattening for value lookup.
  const flatOptions = options.flatMap((o) => (Array.isArray(o.options) ? o.options : [o]));

  return (
    <div className={className}>
      <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">
        {label}
        {required || isRequired && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
      </label>
      <ReactSelect
        options={options}
        isMulti={multiple}
        isClearable={isClearable}
        onChange={handleChange}
        value={
          multiple
            ? flatOptions.filter((o) => value?.includes(o.value))
            : flatOptions.find((o) => o.value === value) ?? null
        }
        styles={CustomSelectStyle}
        {...props}
      />
      {error && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400 flex items-center gap-1">
          <span className="w-1 h-1 rounded-full bg-red-500 inline-block flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
};

export const FormSwitch = ({
  label = "Status",
  checked = false,
  onChange,
  disabled = false,
  className = "",
  when = { active: "Active", inActive: "Inactive" },
}) => {
  const status = checked ? when.active : when.inActive;
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {label && (
        <label className="text-[13px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          {label}
        </label>
      )}
      <div>
        <StatusPill
          value={status}
          variant={checked ? "success" : "neutral"}
          flicker={checked ? {} : undefined}
          disabled={disabled}
          onClick={() => onChange?.(!checked)}
          role="switch"
          aria-checked={checked}
        />
      </div>
    </div>
  );
};

export const FormSection = ({ children, title, className = "" }) => (
  <div className={`space-y-4 ${className}`}>
    {title && (
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 border-b border-orange-200 dark:border-orange-500 pb-2">
        {title}
      </h3>
    )}
    {children}
  </div>
);

